import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../config/supabase'
import { motion } from 'framer-motion'

const SignupForm = ({ onAuthSuccess }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false)
  const [signupEmail, setSignupEmail] = useState('')
  const [recaptchaVerified, setRecaptchaVerified] = useState(false)
  const recaptchaWidgetId = useRef(null)
  const recaptchaContainer = useRef(null)
  const navigate = useNavigate()

  // Initialize reCAPTCHA
  useEffect(() => {
    window.onRecaptchaVerified = (token) => {
      console.log('reCAPTCHA verified with token:', token)
      setRecaptchaVerified(true)
      setError('')
    }

    const loadRecaptcha = () => {
      if (window.grecaptcha && !recaptchaWidgetId.current) {
        try {
          recaptchaWidgetId.current = window.grecaptcha.render(recaptchaContainer.current, {
            'sitekey': '6LfJsIkrAAAAAGNKx9snqG6ACnN0WGb604eEdRsF',
            'callback': 'onRecaptchaVerified',
            'theme': 'light'
          })
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error)
        }
      }
    }

    if (!window.grecaptcha) {
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onload = loadRecaptcha
      document.body.appendChild(script)
      return () => {
        document.body.removeChild(script)
        delete window.onRecaptchaVerified
      }
    } else {
      loadRecaptcha()
    }
  }, [])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePassword = (password) => {
    return password.length >= 6
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!email || !validateEmail(email.trim())) {
      setError('Please enter a valid email address')
      return
    }

    if (!password || !validatePassword(password)) {
      setError('Password must be at least 6 characters')
      return
    }

    if (!recaptchaVerified) {
      setError('Please complete the reCAPTCHA verification')
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/#/login`,
        }
      })

      if (error) throw error

      if (data.user && !data.session) {
        setSignupEmail(email)
        setShowEmailConfirmation(true)
      } else if (data.user && data.session) {
        onAuthSuccess(data.user)
        navigate('/dashboard')
      }
    } catch (error) {
      console.error('Signup error:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  // Show email confirmation page if user just signed up
  if (showEmailConfirmation) {
    const EmailConfirmation = React.lazy(() => import('./EmailConfirmation'))
    return (
      <React.Suspense fallback={
        <div className="min-h-screen flex items-center justify-center from-blue-50 to-indigo-100 gradient-bg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D59A4C]"></div>
        </div>
      }>
        <EmailConfirmation
          email={signupEmail}
          onBack={() => navigate('/login')}
        />
      </React.Suspense>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center from-blue-50 to-indigo-100 gradient-bg">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://i.imghippo.com/files/EnV7296H.webp" 
              alt="K9 Links Logo" 
              className="h-16 w-auto" 
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-chewy">
            Create Account
          </h1>
          <p className="text-gray-600">
            Sign up to start finding backlink opportunities
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setError('')
              }}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent ${
                error && error.includes('email') ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setError('')
              }}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent ${
                error && error.includes('password') ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
          </div>

          <div className="flex justify-center">
            <div ref={recaptchaContainer}></div>
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D59A4C] text-white py-3 px-4 rounded-lg hover:bg-[#B58C54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D59A4C] hover:text-[#B58C54] font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  )
}

export default SignupForm